import type { QueryRowType } from "@rocicorp/zero";
import { styled } from "goober";
import { useCallback, useMemo, useRef, useState } from "react";
import { workflowIdsByConnector } from "../../../shared/liveConnectorUsage";
import { queries } from "../../../shared/queries";
import { config } from "../../environment";
import { useCurrentUserGrants } from "../../hooks/useCurrentUserGrants";
import { useUserGroupMemberships } from "../../hooks/useUserGroupMemberships";
import { useQuery } from "@rocicorp/zero/react";
import {
  type DisplayableSpace,
  useSpaceDisplayName,
} from "../../hooks/useSpaceDisplayName";
import { useUrlSort } from "../../hooks/useUrlSort";
import {
  connectorGroupKey,
  connectorAuthTypeLabel,
  connectorMatchesQuery,
  connectorTypeKey,
  connectorTypeLabel,
  resolveProduct,
} from "../../lib/connector";
import {
  type ConnectorStatus,
  connectorStatusColors,
  getConnectorStatus,
} from "../../lib/connectorStatus";
import { getWritableScopeIds } from "../../lib/getWritableScopeIds";
import { pluralize } from "../../lib/inflection";
import { spaceIcon } from "../../lib/spaceIcon";
import { useMakeUrl, navigate, routes, useCurrentUrl } from "../../lib/urls";
import { usableSuppliersForSpace } from "../../lib/usableSuppliersForSpace";
import { userDisplayName } from "../../lib/userDisplayName";
import { ConnectorStatusPill } from "../ConnectorStatusPill";
import { Avatar } from "../Avatar";
import { Button } from "../Button";
import { ButtonPress } from "../Button/ButtonPress";
import { ConnectorLogo } from "../ConnectorLogo";
import { ConnectorLogoContainer } from "../ConnectorLogoContainer";
import { ConnectorUsageIcons } from "../ConnectorUsageIcons";
import { Dropdown } from "../Dropdown";
import { EmptyState } from "../EmptyState";
import { FacePile } from "../FacePile";
import { Icon } from "../Icon";
import {
  type FilterDef,
  hasActiveFilters,
  ListFilter,
  ListFilterPills,
  parseMultiFilter,
  toggleMultiFilter,
} from "../ListFilter";
import { ListTable } from "../ListTable";
import { Modal } from "../Modal";
import { Skeleton } from "../Skeleton";
import { StatusBadge } from "../StatusBadge";
import { StatusDot } from "../StatusDot";
import { Text } from "../Text";
import { Tooltip } from "../Tooltip";
import { MainScrollContainer } from "../MainScrollContainer";
import { AvailableToCell } from "../AvailableToCell";
import { DeleteConnectorModalBody } from "./DeleteConnectorModal";

function NoteCell({ note }: { note: string }) {
  const ref = useRef<HTMLDivElement>(null);
  const [truncated, setTruncated] = useState(false);

  const content = (
    <div
      ref={ref}
      onMouseEnter={() => {
        const el = ref.current?.firstElementChild;
        if (el) setTruncated(el.scrollHeight > el.clientHeight);
      }}
    >
      <Text
        variant="text-small"
        color="var(--text-secondary)"
        style={{
          display: "-webkit-box",
          WebkitLineClamp: 2,
          WebkitBoxOrient: "vertical",
          overflow: "hidden",
          overflowWrap: "anywhere",
        }}
      >
        {note}
      </Text>
    </div>
  );

  if (!truncated) return content;

  return (
    <Tooltip content={note} align="left">
      {content}
    </Tooltip>
  );
}

type ConnectorRow = QueryRowType<ReturnType<typeof queries.connectorsIndex>>;
type AppCreator = NonNullable<ConnectorRow["createdBy"]>;

const appStatusOrder = [
  "active",
  "untested",
  "error",
  "draft",
  "disabled",
] as const;
type AppStatusValue = ConnectorStatus;

const appStatusLabels: Record<AppStatusValue, string> = {
  active: "Active",
  untested: "Untested",
  error: "Error",
  draft: "Draft",
  disabled: "Disabled",
};

function AppStatusDot({
  status,
  size,
}: {
  status: AppStatusValue;
  size: number;
}) {
  return <StatusDot color={connectorStatusColors[status]} size={size} />;
}

const LogoStack = styled("div")`
  position: relative;
  width: 32px;
  height: 32px;
  flex-shrink: 0;
`;

const StackTile = styled("div")`
  position: absolute;
  inset: 0;
  border-radius: 8px;
  background: var(--alpha8);
  transform-origin: bottom center;
  transition: transform var(--motion-fast) var(--ease-out);
`;

const LogoFront = styled(StackTile)`
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--surface-secondary);
  outline: 0.5px solid var(--border-alpha);
  box-shadow: var(--elevation-low);
  transform-origin: center;
`;

const GroupTag = styled("span")`
  display: inline-flex;
  align-items: center;
  height: 16px;
  padding: 0 6px;
  border-radius: 8px;
  background: var(--alpha8);
  color: var(--text-secondary);
  flex-shrink: 0;
`;

const statusSummaryOrder: AppStatusValue[] = [
  "error",
  "untested",
  "active",
  "draft",
];

const ConnectorStatusCell = styled("div")`
  container: status-pill / inline-size;
  width: 100%;
  min-width: 0;
`;

const StatusSummaryCircle = styled("div")`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  min-width: 20px;
  width: fit-content;
  height: 20px;
  padding: 0 8px;
  border-radius: 38px;
`;

function GroupStatusSummary({ members }: { members: ConnectorRow[] }) {
  const counts = new Map<AppStatusValue, number>();
  for (const member of members) {
    const status = getConnectorStatus(member);
    counts.set(status, (counts.get(status) ?? 0) + 1);
  }
  const present = statusSummaryOrder.filter((status) => counts.has(status));
  if (!present.length) return null;
  const stackedStatuses = [...present];
  for (const [status, count] of counts) {
    stackedStatuses.push(
      ...Array<AppStatusValue>(
        Math.min(count - 1, 4 - stackedStatuses.length)
      ).fill(status)
    );
  }
  stackedStatuses.reverse();
  const summary = present
    .map((status) => `${counts.get(status)} ${appStatusLabels[status]}`)
    .join(", ");
  return (
    <Tooltip content={summary}>
      <div
        role="img"
        aria-label={`${members.length} connectors: ${summary}`}
        style={{ width: "fit-content" }}
      >
        <FacePile
          itemSize={20}
          compressFraction={5 / 3}
          reverse
          cutout
          outlineWidth={0}
          gap={1.5}
          items={stackedStatuses.map((status, index) => ({
            id: `${status}-${index}`,
            bare: true,
            node: (
              <StatusSummaryCircle
                style={{
                  background: `var(--${connectorStatusColors[status]}-200)`,
                  color: `var(--${connectorStatusColors[status]}-600)`,
                }}
              >
                {index === stackedStatuses.length - 1 && (
                  <Text variant="text-smaller-md">{members.length}</Text>
                )}
              </StatusSummaryCircle>
            ),
          }))}
        />
      </div>
    </Tooltip>
  );
}

export function Connected({ active = true }: { active?: boolean }) {
  const makeUrl = useMakeUrl();
  const [connectorsResult, connectorsDetails] = useQuery(
    queries.connectorsIndex()
  );
  const connectorsSettled = connectorsDetails.type !== "unknown";
  const [shareRequestsRaw] = useQuery(queries.pendingGrantRequests());
  // Usage waits for the connectors to land so the list renders before its
  // counts, and stays unsubscribed while the directory pane is active.
  const [liveUsages, liveUsagesDetails] = useQuery(
    active && connectorsSettled ? queries.connectorUsagesBySpace() : undefined
  );
  const [otherUsages, otherUsagesDetails] = useQuery(
    active && connectorsSettled ? queries.connectorListUsages() : undefined
  );
  const usagesSettled =
    liveUsagesDetails.type !== "unknown" &&
    otherUsagesDetails.type !== "unknown";
  const [currentUser] = useQuery(queries.currentUser());
  const [spaces] = useQuery(queries.spaces({ settingsView: true }));
  const { memberships: userGroupIds } = useUserGroupMemberships();
  const displaySpaceName = useSpaceDisplayName();
  const sharedSpaces = useMemo(
    () => spaces.filter((s) => s.mode !== "personal"),
    [spaces]
  );
  // A connector maps to zero, one, or many spaces. Reuse the same "suppliers a
  // space can use" computation Space settings show (usableSuppliersForSpace),
  // then invert space→connectors into connector→spaces so drift is impossible.
  const usableSpacesByConnector = useMemo(() => {
    const byConnector = new Map<string, DisplayableSpace[]>();
    for (const app of connectorsResult) byConnector.set(app.id, []);
    for (const space of sharedSpaces) {
      for (const app of usableSuppliersForSpace(
        "space_uses_connected_apps",
        space.roleGrantsAsScope,
        connectorsResult,
        userGroupIds
      )) {
        byConnector.get(app.id)?.push(space);
      }
    }
    return byConnector;
  }, [connectorsResult, sharedSpaces, userGroupIds]);
  const liveWorkflowsByConnector = useMemo(
    () => workflowIdsByConnector(liveUsages ?? []),
    [liveUsages]
  );
  const usagesByConnector = useMemo(
    () => new Map((otherUsages ?? []).map((app) => [app.id, app.usages])),
    [otherUsages]
  );
  const usedByCount = useCallback(
    (app: ConnectorRow) =>
      (liveWorkflowsByConnector.get(app.id)?.size ?? 0) +
      (usagesByConnector.get(app.id)?.length ?? 0),
    [liveWorkflowsByConnector, usagesByConnector]
  );
  // A requester's own pending share requests keep their connector in the list,
  // flagged "Awaiting approval" so they can track the request. Reviewing pending
  // requests lives in the Monitoring → Requests tab, not an in-context group.
  const myPendingConnectorIds = useMemo(
    () =>
      new Set(
        currentUser
          ? shareRequestsRaw
              .filter((r) => r.requestedByUserId === currentUser.id)
              .map((r) => r.scopeId)
          : []
      ),
    [currentUser, shareRequestsRaw]
  );
  const {
    q: query,
    status: statusParam,
    type: typeParam,
    creator: creatorParam,
    space: spaceParam,
  } = useCurrentUrl(routes.connectors.connected);
  const statusFilter: AppStatusValue | null = (
    appStatusOrder as readonly string[]
  ).includes(statusParam)
    ? (statusParam as AppStatusValue)
    : null;
  const typeFilters = useMemo(() => parseMultiFilter(typeParam), [typeParam]);
  const creatorFilters = useMemo(
    () => parseMultiFilter(creatorParam),
    [creatorParam]
  );
  const spaceFilters = useMemo(
    () => parseMultiFilter(spaceParam),
    [spaceParam]
  );
  const { sortColumn, sortDirection, handleSort, sortableColumns } = useUrlSort(
    routes.connectors.connected,
    {
      name: "asc",
      usedBy: "asc",
      addedBy: "asc",
      createdAt: "desc",
    }
  );
  const [confirmDeleteApp, setConfirmDeleteApp] = useState<{
    id: string;
    oauthConnectionId?: string;
  } | null>(null);
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(
    () => new Set()
  );

  const [myGrants] = useCurrentUserGrants();
  const writableAppIds = useMemo(
    () =>
      getWritableScopeIds(
        myGrants,
        connectorsResult.map((app) => app.id),
        "connected_app:write"
      ),
    [myGrants, connectorsResult]
  );

  const queryFilteredApps = useMemo(() => {
    if (!query) return connectorsResult;
    const lowerQuery = query.toLowerCase();
    return connectorsResult.filter((app) =>
      connectorMatchesQuery(app, lowerQuery)
    );
  }, [connectorsResult, query]);

  const availableStatuses = new Set<AppStatusValue>();
  const availableTypes = new Map<string, string>();
  const availableCreators = new Map<string, AppCreator>();
  const availableSpaceIds = new Set<string>();
  for (const app of queryFilteredApps) {
    availableStatuses.add(getConnectorStatus(app));
    availableTypes.set(connectorTypeKey(app), connectorTypeLabel(app));
    if (app.createdByUserId && app.createdBy) {
      availableCreators.set(app.createdByUserId, app.createdBy);
    }
    for (const space of usableSpacesByConnector.get(app.id) ?? []) {
      availableSpaceIds.add(space.id);
    }
  }
  const availableStatusOptions = appStatusOrder.filter((s) =>
    availableStatuses.has(s)
  );
  const availableTypeOptions = [...availableTypes]
    .map(([identifier, name]) => ({ identifier, name }))
    .sort((a, b) => a.name.localeCompare(b.name));
  const availableCreatorOptions = [...availableCreators.values()].sort((a, b) =>
    userDisplayName(a).localeCompare(userDisplayName(b))
  );
  const availableSpaceOptions = sharedSpaces.filter((s) =>
    availableSpaceIds.has(s.id)
  );

  const connectors = useMemo(
    () =>
      queryFilteredApps.filter((app) => {
        if (statusFilter !== null && getConnectorStatus(app) !== statusFilter)
          return false;
        if (
          typeFilters.length > 0 &&
          !typeFilters.includes(connectorTypeKey(app))
        )
          return false;
        if (creatorFilters.length > 0) {
          if (
            !app.createdByUserId ||
            !creatorFilters.includes(app.createdByUserId)
          )
            return false;
        }
        if (spaceFilters.length > 0) {
          const usable = usableSpacesByConnector.get(app.id);
          if (!usable?.some((space) => spaceFilters.includes(space.id)))
            return false;
        }
        return true;
      }),
    [
      queryFilteredApps,
      statusFilter,
      typeFilters,
      creatorFilters,
      spaceFilters,
      usableSpacesByConnector,
    ]
  );

  const spaceFilter: FilterDef = {
    kind: "multi",
    key: "space",
    label: "Available to",
    icon: <Icon name="hash" size={16} />,
    options: availableSpaceOptions.map((space) => ({
      id: space.id,
      label: displaySpaceName(space),
      menuLeft: <Icon name={spaceIcon(space.mode)} size={16} />,
      pillLeft: <Icon name={spaceIcon(space.mode)} size={12} />,
    })),
    values: spaceFilters,
    onToggle: (id) =>
      navigate(
        null,
        { space: toggleMultiFilter(spaceFilters, id) },
        { replace: true }
      ),
  };

  const filters: FilterDef[] = [
    {
      kind: "multi",
      key: "type",
      label: "Type",
      icon: <Icon name="api-key-custom" size={16} />,
      options: availableTypeOptions.map((type) => ({
        id: type.identifier,
        label: type.name,
        menuLeft: <ConnectorLogo template={type} size={16} />,
        pillLeft: <ConnectorLogo template={type} size={12} />,
      })),
      values: typeFilters,
      onToggle: (id) =>
        navigate(
          null,
          { type: toggleMultiFilter(typeFilters, id) },
          { replace: true }
        ),
    },
    {
      kind: "single",
      key: "status",
      label: "Status",
      icon: <Icon name="circle-dashed" size={16} />,
      options: availableStatusOptions.map((option) => ({
        id: option,
        label: appStatusLabels[option],
        menuLeft: <AppStatusDot status={option} size={12} />,
        pillLeft: <AppStatusDot status={option} size={10} />,
      })),
      value: statusFilter,
      onSelect: (id) => navigate(null, { status: id }, { replace: true }),
      onClear: () => navigate(null, { status: "all" }, { replace: true }),
    },
    ...(availableSpaceOptions.length > 0 || spaceFilters.length > 0
      ? [spaceFilter]
      : []),
    {
      kind: "multi",
      key: "creator",
      label: "Added by",
      icon: <Icon name="circle-user-round" size={16} />,
      options: availableCreatorOptions.map((creator) => ({
        id: creator.id,
        label: userDisplayName(creator),
        menuLeft: <Avatar user={creator} size={16} />,
        pillLeft: <Avatar user={creator} size={12} />,
      })),
      values: creatorFilters,
      onToggle: (id) =>
        navigate(
          null,
          { creator: toggleMultiFilter(creatorFilters, id) },
          { replace: true }
        ),
    },
  ];

  const compareApps = useCallback(
    (a: ConnectorRow, b: ConnectorRow) => {
      if (!sortColumn) return b.createdAt - a.createdAt;

      let aVal: string | number;
      let bVal: string | number;

      switch (sortColumn) {
        case "name":
          aVal = a.name.toLowerCase();
          bVal = b.name.toLowerCase();
          break;
        case "usedBy":
          aVal = usedByCount(a);
          bVal = usedByCount(b);
          break;
        case "addedBy":
          aVal = a.createdBy ? userDisplayName(a.createdBy).toLowerCase() : "";
          bVal = b.createdBy ? userDisplayName(b.createdBy).toLowerCase() : "";
          break;
        case "createdAt":
          aVal = a.createdAt;
          bVal = b.createdAt;
          break;
        default:
          return 0;
      }

      if (aVal < bVal) return sortDirection === "asc" ? -1 : 1;
      if (aVal > bVal) return sortDirection === "asc" ? 1 : -1;
      return 0;
    },
    [sortColumn, sortDirection, usedByCount]
  );

  const groups = useMemo(() => {
    const sortedApps = [...connectors].sort(compareApps);
    const groupsByKey = new Map<
      string,
      { key: string; label: string; rep: ConnectorRow; members: ConnectorRow[] }
    >();
    for (const app of sortedApps) {
      const key = connectorGroupKey(app);
      const group = groupsByKey.get(key);
      if (group) group.members.push(app);
      else
        groupsByKey.set(key, {
          key,
          label: connectorTypeLabel(app),
          rep: app,
          members: [app],
        });
    }
    return [...groupsByKey.values()].sort((a, b) => {
      if (sortColumn && sortColumn !== "name") return compareApps(a.rep, b.rep);
      const cmp = a.label.localeCompare(b.label);
      return sortColumn === "name" && sortDirection === "desc" ? -cmp : cmp;
    });
  }, [connectors, compareApps, sortColumn, sortDirection]);

  const rollupKeys = useMemo(
    () => groups.filter((g) => g.members.length > 1).map((g) => g.key),
    [groups]
  );
  // Drop expanded keys for groups that no longer roll up.
  const prunedExpanded = new Set(
    [...expandedGroups].filter((k) => rollupKeys.includes(k))
  );
  if (prunedExpanded.size !== expandedGroups.size) {
    setExpandedGroups(prunedExpanded);
  }
  const searchActive = !!query;
  const allExpanded =
    rollupKeys.length > 0 && rollupKeys.every((key) => expandedGroups.has(key));
  const toggleGroup = useCallback(
    (key: string) =>
      setExpandedGroups((prev) => {
        const next = new Set(prev);
        if (next.has(key)) next.delete(key);
        else next.add(key);
        return next;
      }),
    []
  );
  const toggleAll = () =>
    setExpandedGroups(allExpanded ? new Set() : new Set(rollupKeys));

  const renderConnectorRow = useCallback(
    (app: ConnectorRow, reveal?: { collapsed: boolean; index: number }) => ({
      key: app.id,
      link: makeUrl(routes.modal.connector, { id: app.id }),
      collapsed: reveal?.collapsed,
      revealIndex: reveal?.index,
      name: (hover: boolean) => (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 12,
            minWidth: 0,
          }}
        >
          <ConnectorLogoContainer
            size={32}
            hover={hover}
            borderRadius={8}
            outline={false}
            hoverBackground={false}
          >
            <ConnectorLogo app={app} size={16} />
          </ConnectorLogoContainer>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 2,
              minWidth: 0,
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                minWidth: 0,
              }}
            >
              <Text
                ellipsis
                variant="text-regular-md"
                color="var(--text-primary)"
              >
                {app.name}
              </Text>
              {myPendingConnectorIds.has(app.id) && (
                <StatusBadge color="orange">Awaiting approval</StatusBadge>
              )}
            </div>
            <Text ellipsis variant="text-small" color="var(--text-secondary)">
              {connectorAuthTypeLabel(app)}
            </Text>
          </div>
        </div>
      ),
      note: app.note ? <NoteCell note={app.note} /> : null,
      status: (
        <ConnectorStatusCell>
          <ConnectorStatusPill app={app} showDetails />
        </ConnectorStatusCell>
      ),
      usedBy: !usagesSettled ? (
        <Skeleton style={{ width: 72 }} />
      ) : usedByCount(app) > 0 ? (
        <ConnectorUsageIcons
          collapseToFit
          workflowCount={liveWorkflowsByConnector.get(app.id)?.size ?? 0}
          usages={usagesByConnector.get(app.id) ?? []}
        />
      ) : (
        <Text variant="text-small">—</Text>
      ),
      space: (
        <AvailableToCell
          spaces={usableSpacesByConnector.get(app.id) ?? []}
          sharedSpaceCount={sharedSpaces.length}
          displaySpaceName={displaySpaceName}
        />
      ),
      addedBy: (
        <Text variant="text-small" color="var(--text-primary)" ellipsis>
          {app.createdBy ? userDisplayName(app.createdBy) : undefined}
        </Text>
      ),
      createdAt: (
        <Text variant="text-small">
          {new Date(app.createdAt).toLocaleDateString()}
        </Text>
      ),
      actions: (hasMouse: boolean) => {
        const canWrite = writableAppIds?.has(app.id) ?? false;
        if (!canWrite) return null;
        return (
          <div style={{ marginLeft: "auto", opacity: hasMouse ? 1 : 0 }}>
            <Dropdown
              opener={<Button variant="secondary" icon="more" />}
              minWidth={160}
              sections={[
                {
                  items: [
                    {
                      type: "button",
                      left: <Icon name="pencil" size={16} />,
                      label: "Edit",
                      onClick: () =>
                        navigate(routes.modal.connector, { id: app.id }),
                    },
                    {
                      type: "button",
                      left: <Icon name="trash" size={16} />,
                      label: "Delete",
                      onClick: () => {
                        setConfirmDeleteApp({
                          id: app.id,
                          oauthConnectionId: app.oauthConnection?.id,
                        });
                      },
                    },
                  ],
                },
              ]}
            />
          </div>
        );
      },
    }),
    [
      liveWorkflowsByConnector,
      usagesByConnector,
      usagesSettled,
      myPendingConnectorIds,
      usedByCount,
      writableAppIds,
      usableSpacesByConnector,
      sharedSpaces,
      displaySpaceName,
      makeUrl,
    ]
  );

  // Search results render flat: every row is a match, and a member's type
  // label already says what a group header would.
  const groupItems = useMemo(
    () =>
      groups.flatMap((group) => {
        if (group.members.length === 1 || searchActive) {
          return group.members.map((member) => renderConnectorRow(member));
        }
        const expanded = expandedGroups.has(group.key);
        const count = group.members.length;
        const repProduct = resolveProduct(group.rep);
        const header = {
          key: `group:${group.key}`,
          onClick: () => toggleGroup(group.key),
          name: (hover: boolean) => (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 12,
                minWidth: 0,
              }}
            >
              <LogoStack>
                <StackTile
                  style={{
                    transform: hover
                      ? "translate(1px, 2px) rotate(-20deg)"
                      : "translate(1px, 2px) rotate(-14deg)",
                  }}
                />
                <StackTile
                  style={{
                    transform: hover
                      ? "translate(-1px, 4px) rotate(20deg)"
                      : "translate(-1px, 4px) rotate(14deg)",
                  }}
                />
                <LogoFront
                  style={{ transform: hover ? "scale(1.08)" : undefined }}
                >
                  {repProduct ? (
                    <ConnectorLogo
                      template={{ name: group.label, identifier: repProduct }}
                      size={16}
                    />
                  ) : (
                    <ConnectorLogo app={group.rep} size={16} />
                  )}
                </LogoFront>
              </LogoStack>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 2,
                  minWidth: 0,
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                    minWidth: 0,
                  }}
                >
                  <Text
                    ellipsis
                    variant="text-regular-md"
                    color="var(--text-primary)"
                  >
                    {group.label}
                  </Text>
                  <GroupTag>
                    <Text variant="text-smaller-md">
                      {`${count} ${pluralize("connector", count)}`}
                    </Text>
                  </GroupTag>
                </div>
                <Text
                  ellipsis
                  variant="text-small"
                  color="var(--text-secondary)"
                >
                  {connectorTypeLabel(group.rep)}
                </Text>
              </div>
            </div>
          ),
          note: null,
          status: <GroupStatusSummary members={group.members} />,
          usedBy: null,
          // Availability is per-connector; a group's rolled-up union would read
          // as a guarantee for every member, so it's shown only on leaf rows.
          space: null,
          addedBy: null,
          createdAt: null,
          actions: (
            <div
              style={{
                marginLeft: "auto",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: 24,
                height: 24,
              }}
            >
              <Icon
                name="chevron-right"
                size={16}
                color="var(--text-secondary)"
                style={{
                  transition: "transform var(--motion-fast) var(--ease-out)",
                  transform: expanded ? "rotate(90deg)" : undefined,
                }}
              />
            </div>
          ),
        };
        return [
          header,
          ...group.members.map((member, i) =>
            renderConnectorRow(member, { collapsed: !expanded, index: i })
          ),
        ];
      }),
    [groups, searchActive, expandedGroups, renderConnectorRow, toggleGroup]
  );

  const items = groupItems;

  return (
    <>
      {active && <title>{`Connectors • ${config.title}`}</title>}
      <MainScrollContainer
        active={active}
        ready={connectorsSettled}
        resetOnNavigate={false}
      >
        <ListTable
          style={{ borderRadius: 0, borderLeft: 0, borderRight: 0 }}
          columns={{ note: "md", space: "md" }}
          footer={
            hasActiveFilters(filters) ? (
              <ListFilterPills filters={filters} />
            ) : null
          }
          headerActions={
            <>
              {rollupKeys.length > 0 && !searchActive ? (
                <Tooltip content={allExpanded ? "Collapse all" : "Expand all"}>
                  <ButtonPress
                    variant="secondary"
                    icon={allExpanded ? "chevron-up" : "chevron-down"}
                    onPress={toggleAll}
                  />
                </Tooltip>
              ) : null}
              <ListFilter filters={filters} />
            </>
          }
          emptyState={
            connectorsDetails.type === "complete" ? (
              connectorsResult.length > 0 ? (
                <EmptyState
                  iconName="api-key-custom"
                  title="No matches"
                  description="Try a different filter."
                />
              ) : !query ? (
                <EmptyState
                  iconName="api-key-custom"
                  title="No connectors"
                  description="Add a connector to use it in workflows."
                />
              ) : undefined
            ) : undefined
          }
          headers={{
            name: "Connector name",
            note: "Note",
            status: "Status",
            space: "Available to",
            usedBy: "Used by",
            addedBy: "Added by",
            createdAt: "Created at",
            actions: "",
          }}
          sortableColumns={sortableColumns}
          dateColumns={["createdAt"]}
          sortColumn={sortColumn}
          sortDirection={sortDirection}
          onSort={handleSort}
          items={items}
        />
      </MainScrollContainer>
      <Modal
        open={confirmDeleteApp !== null}
        onClose={() => setConfirmDeleteApp(null)}
        alert
      >
        {confirmDeleteApp && (
          <DeleteConnectorModalBody
            appId={confirmDeleteApp.id}
            onClose={() => setConfirmDeleteApp(null)}
          />
        )}
      </Modal>
    </>
  );
}
